/**
 * TanStack Query — app-wide singleton.
 *
 * The defaults mirror the mobile app's (`move-with-me/src/shared/lib/query-client.ts`) where
 * they still make sense, so the same surface behaves the same on both clients:
 * a 30 s `staleTime` that feature hooks override per surface, a generous `gcTime` so
 * re-entering a cached screen costs no spinner, one query retry, and no mutation retry.
 *
 * What deliberately does NOT port: the AppState and expo-network wiring. React Native has no
 * `window`, so mobile has to feed `focusManager` from AppState and `onlineManager` from
 * expo-network, and it switches `refetchOnWindowFocus` off to stop the two mechanisms
 * double-firing. In a browser both are native — TanStack Query listens to `visibilitychange`
 * and to the `online`/`offline` events itself — so `refetchOnWindowFocus` is left at its
 * default (on) and nothing is subscribed by hand. Adding a focus manager here would be
 * re-implementing what the library already does correctly.
 */

import { QueryClient } from '@tanstack/react-query';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Feature hooks override per surface. 30 s keeps backend traffic bounded while a tab
      // switch still feels current.
      staleTime: 30_000,
      // Keep the cache hot for 5 minutes after the last observer unmounts, so back-then-
      // forward navigation re-enters with no loading state.
      gcTime: 5 * 60_000,
      // A desk browser refetches on focus and on reconnect for free — both left on.
      refetchOnReconnect: true,
      // One retry is enough; more spams a small backend fleet without improving the
      // success rate.
      retry: 1,
    },
    mutations: {
      // A plan save is a whole-tree replace guarded by an opaque `baseVersion`
      // (plan-builder document-model ADR §2, §4). Retrying one automatically is exactly how
      // a lost concurrency race turns into a silent overwrite — the trainer decides.
      retry: 0,
    },
  },
});
